import { useState } from 'react'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  useEventRaidReports,
  useTeamEvent,
} from 'app/frontend/base/config/queries'
import ViewerPresenceButton from 'app/frontend/base/components/ViewerPresenceButton'
import EventAvatar from 'app/frontend/base/components/EventAvatar'
import AddEventDialog, {
  AddEventFormValues,
} from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEventOverview/components/AddEventDialog'
import EventChangelogDialog from './EventChangelogDialog'
import useEventChannel from '../hooks/useEventChannel'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import { Check, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { useTeamEventUpdateContext } from '../context/TeamEventUpdateContext'
import { useTranslation } from 'react-i18next'
import capitalize from 'lodash/capitalize'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { cn } from '@/lib/utils'
import EventRaidReportsDialog from './EventRaidReportsDialog'
import EventRaidReportsBadge from './EventRaidReportsBadge'
dayjs.extend(RelativeTime)

interface Props {
  eventId?: number
  initialEventReference?: wowaudit.EventReference
  onAddParticipants?: () => void
  onAddActivities?: () => void
  onEventUpdated: (event: wowaudit.Event) => void
}

const EventHeader = ({
  eventId,
  initialEventReference,
  onAddParticipants,
  onAddActivities,
  onEventUpdated,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { accessLevel } = useAuth()
  const { team } = useTeam()
  const queryClient = useQueryClient()
  const { isSaving, editEvent } = useTeamEventUpdateContext()
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isLogOpen, setIsLogOpen] = useState(false)
  const [isReportsOpen, setIsReportsOpen] = useState(false)
  const canEdit = accessLevel === 'admin'
  const { data: event } = useTeamEvent(team.id, eventId)
  const headerEvent = event ?? initialEventReference

  const { viewers, requiresReload } = useEventChannel({
    eventId,
    onEventUpdated,
    onLinkedRaidPlanUpdated: (nextRaidPlan) => {
      queryClient.setQueriesData(
        { queryKey: ['raidPlans'] },
        (previous: RaidPlan[] | undefined) => {
          if (!previous) return previous
          let hasMatch = false
          const next = previous.map((raidPlan) => {
            if (raidPlan.id !== nextRaidPlan.id) return raidPlan
            hasMatch = true
            return nextRaidPlan
          })
          return hasMatch ? next : previous
        },
      )
      const owner = { ownerType: 'team', ownerId: team.id } as const
      queryClient.setQueryData(
        ['raidPlan', { owner, planId: nextRaidPlan.id }],
        nextRaidPlan,
      )
    },
  })

  const eventIdForRaidReports = event?.id ?? headerEvent?.id
  const shouldRenderRaidReportBadge =
    !!headerEvent && dayjs(headerEvent.startsAt).isBefore(dayjs())
  const { data: raidReports, isLoading: isRaidReportsLoading } =
    useEventRaidReports(
      team.id,
      eventIdForRaidReports,
      shouldRenderRaidReportBadge,
    )
  const hasLinkedReport = (raidReports?.length || 0) > 0

  if (!headerEvent) return null

  const statusLabel =
    headerEvent.status === 'planned'
      ? dayjs(headerEvent.startsAt).fromNow()
      : capitalize(headerEvent.status)

  return (
    <div className='mb-4'>
      <div className='flex items-start justify-between gap-4 flex-wrap'>
        <div className='flex items-center gap-3 min-w-0'>
          <EventAvatar
            eventReference={headerEvent}
            variant='square'
            className={cn(
              'w-14 h-14 border border-border shrink-0',
              headerEvent.status === 'cancelled' ? 'grayscale' : '',
            )}
          />
          <div className='min-w-0'>
            <div className='flex items-center gap-2 flex-wrap'>
              <h2 className='truncate m-0 mr-2'>{headerEvent.title}</h2>
              <Badge
                variant='outline'
                className={cn(
                  'text-sm',
                  headerEvent.status === 'locked'
                    ? 'text-warning-foreground'
                    : '',
                  headerEvent.status === 'cancelled'
                    ? 'text-destructive-foreground'
                    : '',
                )}
              >
                {statusLabel}
              </Badge>
              {headerEvent.optional ? (
                <Badge variant='info' className='text-sm'>
                  Optional
                </Badge>
              ) : null}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex'>
                    <Badge variant='outline'>
                      {isSaving ? (
                        <Loader2 className='h-4 w-4 my-0.5 mx-0.5 animate-spin text-muted-foreground' />
                      ) : (
                        <Check className='h-5 w-5 text-success-foreground' />
                      )}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {isSaving ? 'Saving changes...' : 'Up to date'}
                </TooltipContent>
              </Tooltip>
              {shouldRenderRaidReportBadge ? (
                <EventRaidReportsBadge
                  isLoading={isRaidReportsLoading}
                  hasLinkedReport={hasLinkedReport}
                  onClick={() => setIsReportsOpen(true)}
                />
              ) : null}
            </div>
            <div className='mt-0.5 flex items-center gap-2'>
              <Button
                variant='ghost'
                size='icon'
                className='h-7 w-7'
                disabled={!event?.siblings.previous}
                onClick={() => {
                  if (!event?.siblings.previous) return
                  navigate(`${team.path}/events/${event.siblings.previous}`)
                }}
              >
                <ChevronLeft className='h-4 w-4' />
              </Button>
              <span className='w-70 text-center flex items-center justify-between'>
                <span>
                  {dayjs(headerEvent.date).format('dddd, MMM D YYYY')}
                </span>
                <span className='text-xs text-foreground'>
                  {headerEvent.startTime} - {headerEvent.finishTime}
                </span>
              </span>
              <Button
                variant='ghost'
                size='icon'
                className='h-7 w-7'
                disabled={!event?.siblings.next}
                onClick={() => {
                  if (!event?.siblings.next) return
                  navigate(`${team.path}/events/${event.siblings.next}`)
                }}
              >
                <ChevronRight className='h-4 w-4' />
              </Button>
            </div>
          </div>
        </div>

        <div>
          <div className='flex items-center gap-2 flex-wrap'>
            <ViewerPresenceButton viewers={viewers} />
            {canEdit ? (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button variant='outline'>Add</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={onAddParticipants}>
                    Participants
                  </DropdownMenuItem>
                  {event?.activityType === 'raids' ? (
                    <DropdownMenuItem onClick={onAddActivities}>
                      Activities
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {canEdit ? (
              <Button
                variant='outline'
                disabled={!event}
                onClick={() => {
                  setIsEditOpen(true)
                }}
              >
                Edit
              </Button>
            ) : null}
            {canEdit ? (
              <Button
                variant='outline'
                disabled={!event}
                onClick={() => {
                  setIsLogOpen(true)
                }}
              >
                {t('dashboard.team.events.changelog.button')}
              </Button>
            ) : null}
            <Button
              variant='outline'
              onClick={() => navigate(`${team.path}/events`)}
            >
              {t('shared.back-to-list')}
            </Button>
          </div>
        </div>
      </div>

      {canEdit && event ? (
        <AddEventDialog
          open={isEditOpen}
          setOpen={setIsEditOpen}
          title='Edit event'
          submitLabel='Save changes'
          showUpdateSuccessToast
          hideActivityFields
          showActivityChangeFields
          alwaysShowTitleField
          showStatusControls
          defaultValues={event as Partial<AddEventFormValues>}
          onSubmit={async (values) => {
            await editEvent(values)
          }}
          onDeleteEvent={async (values) => {
            await editEvent(values)
          }}
        />
      ) : null}
      {canEdit ? (
        <EventChangelogDialog
          eventId={event?.id}
          open={isLogOpen}
          onOpenChange={setIsLogOpen}
        />
      ) : null}
      <EventRaidReportsDialog
        open={isReportsOpen}
        onOpenChange={setIsReportsOpen}
        reports={raidReports || []}
        event={event}
        isLoading={isRaidReportsLoading}
      />

      <AlertDialog open={requiresReload}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Connection lost</AlertDialogTitle>
            <AlertDialogDescription>
              Live sync is unavailable and could not reconnect. Please reload
              before continuing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                window.location.reload()
              }}
            >
              Reload
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default EventHeader
